import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/payroll/settings")({
  component: RouteComponent,
});

function RouteComponent() {
  // The read endpoint itself is gated by this single capability, with no
  // degrade-to-read-only fallback, so the whole page is protected up front.
  return (
    <ProtectedRoute requiredPermission="salary.setting.manage">
      <PayrollSettingsPage />
    </ProtectedRoute>
  );
}

import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Loader2, Settings2, X } from "lucide-react";
import PageHeader from "@/components/reusable-components/header/page-header";

import useFetchPayrollSetting from "@/hooks/api/salaries/useFetchPayrollSetting";
import useUpdatePayrollSetting from "@/hooks/api/salaries/useUpdatePayrollSetting";
import {
  payrollSettingSchema,
  PAYROLL_SETTING_FIELDS,
  type PayrollSettingFormValues,
} from "@/schemas/salary/payroll-setting.schema";
import { applyServerErrors } from "@/lib/apply-server-errors";

function PayrollSettingsPage() {
  const payrollSettingQuery = useFetchPayrollSetting();
  const payrollSetting = payrollSettingQuery.data ?? null;
  const fetchLoading = payrollSettingQuery.isPending;

  const updatePayrollSetting = useUpdatePayrollSetting();

  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setError,
    formState: { errors },
  } = useForm<PayrollSettingFormValues>({
    resolver: zodResolver(payrollSettingSchema),
    defaultValues: {
      overtime_enabled: true,
      normal_ot_multiplier: "1.30",
      holiday_ot_multiplier: "1.50",
      standard_monthly_hours: "",
      pay_full_salary_without_schedule: false,
    },
  });

  useEffect(() => {
    if (payrollSetting) {
      reset({
        overtime_enabled: payrollSetting.overtime_enabled,
        normal_ot_multiplier: payrollSetting.normal_ot_multiplier,
        holiday_ot_multiplier: payrollSetting.holiday_ot_multiplier,
        standard_monthly_hours: payrollSetting.standard_monthly_hours ?? "",
        pay_full_salary_without_schedule:
          payrollSetting.pay_full_salary_without_schedule ?? false,
      });
    }
  }, [payrollSetting, reset]);

  const handleFormSubmit = async (data: PayrollSettingFormValues) => {
    try {
      await updatePayrollSetting.mutateAsync({
        ...data,
        standard_monthly_hours: data.standard_monthly_hours || null,
      });
      toast.success("Payroll settings updated successfully");
      setIsEditing(false);
    } catch (err) {
      const { handled, rest } = applyServerErrors(
        err,
        setError,
        PAYROLL_SETTING_FIELDS
      );
      if (!handled || rest.length > 0)
        toast.error(rest[0] ?? "Failed to update payroll settings");
    }
  };

  const handleCancel = () => {
    if (payrollSetting) {
      reset({
        overtime_enabled: payrollSetting.overtime_enabled,
        normal_ot_multiplier: payrollSetting.normal_ot_multiplier,
        holiday_ot_multiplier: payrollSetting.holiday_ot_multiplier,
        standard_monthly_hours: payrollSetting.standard_monthly_hours ?? "",
        pay_full_salary_without_schedule:
          payrollSetting.pay_full_salary_without_schedule ?? false,
      });
    }
    setIsEditing(false);
  };

  const isLoading = fetchLoading || updatePayrollSetting.isPending;

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Payroll Settings"
          description="Configure hourly-rate and overtime rules for payroll calculations."
          isLoading={isLoading}
        >
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit(handleFormSubmit)}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {updatePayrollSetting.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Settings2 className="h-4 w-4" />
              Edit Settings
            </Button>
          )}
        </PageHeader>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payroll Configuration</CardTitle>
            <CardDescription className="text-justify">
              These apply across all of Payroll for this organization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {fetchLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-full rounded" />
                <Skeleton className="h-10 w-full rounded" />
                <Skeleton className="h-10 w-full rounded" />
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                className="space-y-4"
              >
                {/* Enable Overtime Field */}
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="overtime_enabled">Enable Overtime</Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      Applied when calculating overtime pay during payslip
                      generation. When disabled, overtime hours are paid at the
                      normal rate (1×).
                    </p>
                  </div>
                  <div className="shrink-0">
                    <Controller
                      name="overtime_enabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="overtime_enabled"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Standard Monthly Hours Field */}
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="standard_monthly_hours">
                      Standard Monthly Hours
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      When set, hourly rate = monthly salary ÷ this. Leave
                      blank to use the employee's rostered hours.
                    </p>
                    {errors.standard_monthly_hours && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.standard_monthly_hours.message}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 w-[90px]">
                    <Input
                      id="standard_monthly_hours"
                      type="number"
                      step="0.01"
                      min="80"
                      max="744"
                      placeholder="e.g., 173"
                      disabled={!isEditing || isLoading}
                      {...register("standard_monthly_hours")}
                    />
                  </div>
                </div>

                {/* Pay Without Schedule Field */}
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="pay_full_salary_without_schedule">
                      Pay Full Salary Without a Schedule
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      When on, a monthly employee with no shifts in the period
                      is paid in full. When off, they are left out of the run
                      and listed so shifts can be assigned first.
                    </p>
                  </div>
                  <div className="shrink-0">
                    <Controller
                      name="pay_full_salary_without_schedule"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="pay_full_salary_without_schedule"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Normal OT Multiplier Field */}
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="normal_ot_multiplier">
                      Normal OT Multiplier
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      Applied to extra hours on regular work days (e.g. 1.30 =
                      1.3× the base hourly rate).
                    </p>
                    {errors.normal_ot_multiplier && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.normal_ot_multiplier.message}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 w-[70px]">
                    <Input
                      id="normal_ot_multiplier"
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="e.g., 1.30"
                      disabled={!isEditing || isLoading}
                      {...register("normal_ot_multiplier")}
                    />
                  </div>
                </div>

                {/* Holiday OT Multiplier Field */}
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="holiday_ot_multiplier">
                      Holiday OT Multiplier
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      Applied to hours worked on off-days and holidays (e.g.
                      1.50 = 1.5× the base hourly rate).
                    </p>
                    {errors.holiday_ot_multiplier && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.holiday_ot_multiplier.message}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 w-[70px]">
                    <Input
                      id="holiday_ot_multiplier"
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="e.g., 1.50"
                      disabled={!isEditing || isLoading}
                      {...register("holiday_ot_multiplier")}
                    />
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </SidebarInset>
  );
}
